import type { Locale } from "@/lib/i18n";
import type { EntitlementAccessScope } from "@/features/dashboard/types";
import type { CourseLesson } from "../api";
import type { CurriculumModule } from "./curriculum-props";
import {
  type CoursePhaseInfo,
  buildModuleToPhaseMap,
  computeLockedPhaseIds,
} from "./phase-lock";

function localizedPhaseName(phase: CoursePhaseInfo, locale: Locale): string {
  if (locale === "bn" && phase.name.bn?.trim()) return phase.name.bn;
  return phase.name.en;
}

/**
 * @returns `null` when the lesson is not phase-locked from the caller’s perspective.
 */
export function getPhaseLockHint(
  moduleId: string,
  coursePhases: CoursePhaseInfo[],
  lockedPhaseIds: Set<string>,
  moduleToPhase: Map<string, CoursePhaseInfo>,
  locale: Locale,
): string | null {
  const phase = moduleToPhase.get(moduleId);
  if (!phase || !lockedPhaseIds.has(phase.id)) return null;
  if (!phase.prerequisitePhaseId) {
    return "Complete the prerequisite phase to unlock";
  }
  const prereq = coursePhases.find((p) => p.id === phase.prerequisitePhaseId);
  const name = prereq ? localizedPhaseName(prereq, locale) : "the prerequisite phase";
  return `Complete ${name} to unlock`;
}

export function isModuleInEntitlementScope(
  accessScope: EntitlementAccessScope | undefined,
  moduleId: string,
  moduleToPhase: Map<string, CoursePhaseInfo>,
): boolean {
  if (accessScope === undefined || accessScope.type === "full") {
    return true;
  }
  if (accessScope.type === "modules") {
    return (accessScope.moduleIds ?? []).includes(moduleId);
  }
  if (accessScope.type === "phases") {
    const ph = moduleToPhase.get(moduleId);
    if (!ph) return false;
    return (accessScope.phaseIds ?? []).includes(ph.id);
  }
  return true;
}

export type CurriculumRowMode =
  | "public_preview"
  | "public_locked"
  | "owned_open"
  | "owned_plan_locked"
  | "owned_phase_locked"
  | "owned_drip_locked";

export type CurriculumLessonRowModel = {
  mode: CurriculumRowMode;
  /** Native `title` / tooltip */
  hint: string | null;
  showFreePreviewBadge: boolean;
  showLockIcon: boolean;
  linkHref: string | null;
};

type ResolveArgs = {
  courseSlug: string;
  locale: Locale;
  isOwned: boolean;
  accessScope: EntitlementAccessScope | undefined;
  lesson: Pick<CourseLesson, "_id" | "freePreview">;
  moduleId: string;
  courseModules: CurriculumModule[];
  coursePhases: CoursePhaseInfo[] | undefined;
  completedLessonIds: Set<string>;
  /** When set after plan + phase checks, blocks links for drip-scheduled lessons */
  dripBlocked?: boolean;
  dripHint?: string | null;
};

/**
 * Centralizes curriculum row state for the public course page and is fully covered by unit tests.
 */
export function resolveCurriculumLessonRowModel({
  courseSlug,
  locale,
  isOwned,
  accessScope,
  lesson,
  moduleId,
  courseModules,
  coursePhases,
  completedLessonIds,
  dripBlocked = false,
  dripHint = null,
}: ResolveArgs): CurriculumLessonRowModel {
  const base = `/${locale}/courses/${courseSlug}/lessons/${lesson._id}`;

  if (!isOwned) {
    if (lesson.freePreview) {
      return {
        mode: "public_preview",
        hint: null,
        showFreePreviewBadge: true,
        showLockIcon: false,
        linkHref: base,
      };
    }
    return {
      mode: "public_locked",
      hint: "Enroll to access",
      showFreePreviewBadge: false,
      showLockIcon: true,
      linkHref: null,
    };
  }

  const phases = (coursePhases ?? []) as CoursePhaseInfo[];
  const sortedPhases = phases.slice().sort((a, b) => a.order - b.order);
  const moduleToPhase = buildModuleToPhaseMap(sortedPhases);
  const lockedPhaseIds =
    sortedPhases.length > 0
      ? computeLockedPhaseIds(sortedPhases, courseModules, completedLessonIds)
      : new Set<string>();

  if (!isModuleInEntitlementScope(accessScope, moduleId, moduleToPhase)) {
    return {
      mode: "owned_plan_locked",
      hint: "Not included in your plan. Upgrade to unlock.",
      showFreePreviewBadge: false,
      showLockIcon: true,
      linkHref: null,
    };
  }

  const phaseHint = getPhaseLockHint(
    moduleId,
    sortedPhases,
    lockedPhaseIds,
    moduleToPhase,
    locale,
  );
  if (phaseHint) {
    return {
      mode: "owned_phase_locked",
      hint: phaseHint,
      showFreePreviewBadge: false,
      showLockIcon: true,
      linkHref: null,
    };
  }

  if (dripBlocked) {
    return {
      mode: "owned_drip_locked",
      hint: dripHint ?? "Releases on a schedule",
      showFreePreviewBadge: false,
      showLockIcon: true,
      linkHref: null,
    };
  }

  return {
    mode: "owned_open",
    hint: null,
    showFreePreviewBadge: false,
    showLockIcon: false,
    linkHref: base,
  };
}
